import { PriceVariant } from '@/integrations/crystallize/types';
import { SubscriptionPeriodPricingFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import {
  filterHiddenByCountry,
  getSoleAvailableOption,
  isHiddenByCountry,
  resolveCountryVisibility,
} from '@/utils/tile-country-visibility';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';
import { ParsedHardwareProductVariant } from '../components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { ColorDetail } from '../components/pages/ProductDetail/types/ColorDetail';
import { AttributeTileDetail } from '../components/pages/ProductDetail/types/attribute-tile-detail';
import type { SelectedProductVariants } from '../components/pages/ProductDetail/types/SelectedProductVariants';
import { isProductVariantInStock } from './utils/isVariantInStock';

// What a bundle slot lets the visitor choose: its colour, its cylinder or the variant itself.
export type BundleSlotAttribute = 'color' | 'cylinder' | 'variant';

// How a bundle slot offers its variants: colour swatches when every variant carries
// its own colour, otherwise one tile per variant (showing its cylinder when every variant
// carries its own); a slot with one variant offers none.
export type BundleSlotOptions =
  | { kind: 'color'; colors: ColorDetail[] }
  | { kind: 'variant'; tiles: AttributeTileDetail[] }
  | { kind: 'none' };

export interface BundleProductInit {
  bundleProductInstanceId: string;
  id: string;
  shape: string;
  path: string;
  name: string;
  discount: number;
  variants: ParsedHardwareProductVariant[];
}

export class BundleProduct {
  bundleProductInstanceId: string;

  id: string;

  shape: string;

  path: string;

  name: string;

  discount: number;

  variants: ParsedHardwareProductVariant[];

  constructor({
    bundleProductInstanceId,
    id,
    shape,
    path,
    name,
    discount,
    variants,
  }: BundleProductInit) {
    this.bundleProductInstanceId = bundleProductInstanceId;
    this.id = id;
    this.shape = shape;
    this.path = path;
    this.name = name;
    this.discount = discount;
    this.variants = variants;
  }

  get priceVariant(): PriceVariant | undefined {
    return this.defaultVariant?.priceVariant;
  }

  get defaultVariant(): ParsedHardwareProductVariant {
    const defaultVariant = this.variants.find((variant) => variant.isDefault);

    return defaultVariant || this.variants[0]; // Return default variant or the first one
  }

  get priceAfterDiscount(): number {
    return this.getPriceAfterDiscount();
  }

  // Only variants that can go into the cart are offered; the default variant always stays
  getPurchasableVariants(): ParsedHardwareProductVariant[] {
    const { defaultVariant } = this;
    return this.variants.filter(
      (variant) =>
        variant === defaultVariant || !!variant.priceVariant || !!variant.subscriptionPlan,
    );
  }

  isSelectable(): boolean {
    return this.getPurchasableVariants().length > 1;
  }

  isColorSlot(): boolean {
    return this.isSelectable() && !!this.getUniqueVariantColors();
  }

  // Colour wins when variants differ by both, so the colour rule stays as it was.
  isCylinderSlot(): boolean {
    return this.isSelectable() && !this.isColorSlot() && !!this.getUniqueVariantCylinders();
  }

  getSlotAttribute(): BundleSlotAttribute {
    if (this.isColorSlot()) {
      return 'color';
    }

    return this.isCylinderSlot() ? 'cylinder' : 'variant';
  }

  // The selected variant of this slot when the slot offers it, the default variant otherwise
  resolveVariant(selected?: SelectedProductVariants): ParsedHardwareProductVariant {
    const sku = selected?.[this.bundleProductInstanceId]?.sku;
    return (sku && this.getPurchasableVariantBySKU(sku)) || this.defaultVariant;
  }

  // Selections from outside the pickers (URL, direct purchase) only accept what the pickers offer
  getPurchasableVariantBySKU(sku: string): ParsedHardwareProductVariant | undefined {
    return this.getPurchasableVariants().find((variant) => variant.sku === sku);
  }

  // Base price of the given variant, the default variant when none is given.
  // Subscriptions have no price variant and fall back to their initial period.
  getVariantPrice(variant?: ParsedProductVariant): number {
    const priced = variant ?? this.defaultVariant;
    return priced?.priceVariant?.price || priced?.subscriptionPlan?.periods[0]?.initial?.price || 0;
  }

  getPriceAfterDiscount(variant?: ParsedProductVariant): number {
    const priced = variant ?? this.defaultVariant;
    return Math.max(0, (priced?.priceVariant?.price || 0) - this.discount);
  }

  // A country hides the cylinder tiles it is configured to hide, like the product's own
  // cylinder picker; without a country every option stays.
  getSlotOptions(country?: string | null): BundleSlotOptions {
    if (!this.isSelectable()) {
      return { kind: 'none' };
    }

    const colors = this.getUniqueVariantColors();
    if (colors) {
      return { kind: 'color', colors };
    }

    // Cylinder tiles stay keyed by SKU, so choosing one maps back to its variant like any tile.
    if (this.isCylinderSlot()) {
      return {
        kind: 'variant',
        tiles: filterHiddenByCountry(
          this.getPurchasableVariants().flatMap((variant) => {
            const { cylinder } = variant;
            return cylinder ? [{ ...cylinder, type: variant.sku }] : [];
          }),
          country,
        ),
      };
    }

    return {
      kind: 'variant',
      tiles: this.getPurchasableVariants().map((variant) => ({
        type: variant.sku,
        name: variant.name,
        image: variant.images[0],
      })),
    };
  }

  // A variant whose cylinder the shop withholds in the country does not sell there,
  // whether its tile is hidden or shown with the unavailability notice.
  isVariantRestrictedInCountry(variant: ParsedProductVariant, country?: string | null): boolean {
    const cylinder = this.getVariantBySKU(variant.sku)?.cylinder;
    return !!cylinder && resolveCountryVisibility(cylinder, country) !== 'visible';
  }

  // The variant of the only cylinder the country fully allows, when exactly one does.
  getSoleVisibleVariant(country?: string | null): ParsedHardwareProductVariant | undefined {
    const options = this.getSlotOptions(country);
    if (!this.isCylinderSlot() || options.kind !== 'variant') {
      return undefined;
    }

    const sole = getSoleAvailableOption(options.tiles, country);
    return sole ? this.getPurchasableVariantBySKU(sole.type) : undefined;
  }

  // A selection whose tile the country hides is snapped to the sole visible option, as the
  // product page does; undefined when it needs no snap or has no single option to go to.
  getCountrySnapVariant(
    variant: ParsedProductVariant,
    country?: string | null,
  ): ParsedHardwareProductVariant | undefined {
    const cylinder = this.getVariantBySKU(variant.sku)?.cylinder;
    if (!cylinder || !isHiddenByCountry(cylinder, country)) {
      return undefined;
    }

    const sole = this.getSoleVisibleVariant(country);
    return sole && sole.sku !== variant.sku ? sole : undefined;
  }

  getVariantForOption(
    option: ColorDetail | AttributeTileDetail,
  ): ParsedHardwareProductVariant | undefined {
    if ('hex' in option) {
      return this.getPurchasableVariants().find((variant) => variant.color?.name === option.name);
    }
    return this.getPurchasableVariantBySKU(option.type);
  }

  getSelectedValueLabel(variant: ParsedProductVariant): string {
    if (this.isColorSlot()) {
      return this.getVariantBySKU(variant.sku)?.color?.name ?? variant.name;
    }

    if (this.isCylinderSlot()) {
      return this.getVariantBySKU(variant.sku)?.cylinder?.name ?? variant.name;
    }

    return variant.name;
  }

  // The colours of all purchasable variants, or undefined when one has none or two share one.
  private getUniqueVariantColors(): ColorDetail[] | undefined {
    const colors = this.getPurchasableVariants().map((variant) => variant.color);
    if (!colors.every((color): color is ColorDetail => !!color)) {
      return undefined;
    }

    const names = new Set(colors.map((color) => color.name));
    return names.size === colors.length ? colors : undefined;
  }

  // The cylinders of all purchasable variants, or undefined when one has none or two share a type.
  private getUniqueVariantCylinders(): AttributeTileDetail[] | undefined {
    const cylinders = this.getPurchasableVariants().map((variant) => variant.cylinder);
    if (!cylinders.every((cylinder): cylinder is AttributeTileDetail => !!cylinder)) {
      return undefined;
    }

    const types = new Set(cylinders.map((cylinder) => cylinder.type));
    return types.size === cylinders.length ? cylinders : undefined;
  }

  get isFree(): Boolean {
    return this.priceAfterDiscount === 0;
  }

  get isSubscription(): Boolean {
    return this.variants.some((variant) => variant.subscriptionPlan);
  }

  get hasFreeTrial(): Boolean {
    return (
      this.isSubscription &&
      this.variants.some((variant) => variant.subscriptionPlan?.periods[0].initial?.price === 0)
    );
  }

  get initialPeriod(): SubscriptionPeriodPricingFragment | undefined | null {
    return this.defaultVariant.subscriptionPlan?.periods[0]?.initial;
  }

  get recurringPeriod(): SubscriptionPeriodPricingFragment | undefined | null {
    return this.defaultVariant.subscriptionPlan?.periods[0]?.recurring;
  }

  getVariantBySKU(sku: string) {
    return this.variants.find((variant) => variant.sku === sku);
  }

  isVariantInStock(sku: string) {
    const variant = this.getVariantBySKU(sku);
    return isProductVariantInStock(variant);
  }

  isSomeVariantInStock(): boolean {
    return this.variants.some((variant) => isProductVariantInStock(variant));
  }
}
